import { isNullOrUndefined } from "csi/utility/coreUtility";
import { DateRangeSelection } from "csi/utility/dateUtility";

const RE_CONTAINER = /(list|map|set|vector)$/;
const RE_MAP = /map$/;

/** types of attribute containers */
export const VALUE_CONTAINER_TYPE = {
	LIST: "list",
	MAP: "map",
	SET: "set",
	VECTOR: "vector",
};

/** type for an attribute value */
export const VALUE_TYPE = {
	BOOLEAN: "boolean",
	BOOLEAN_LIST: "booleanlist",
	BOOLEAN_VECT: "booleanvector",
	BOOLEAN_SET: "booleanset",
	BOOLEAN_MAP: "booleanmap",

	CNL: "cnl",
	CNL_LIST: "cnllist",
	CNL_VECT: "cnlvector",
	CNL_SET: "cnlset",
	CNL_MAP: "cnlmap",

	DOUBLE: "double",
	DOUBLE_LIST: "doublelist",
	DOUBLE_VECT: "doublevector",
	DOUBLE_SET: "doubleset",
	DOUBLE_MAP: "doublemap",

	ENUM: "enum",
	ENUM_LIST: "enumlist",
	ENUM_VECT: "enumvector",
	ENUM_SET: "enumset",
	ENUM_MAP: "enummap",

	EXPRESSION: "expression",

	INT64: "int64",
	INT64_LIST: "int64list",
	INT64_VECT: "int64vector",
	INT64_SET: "int64set",
	INT64_MAP: "int64map",

	INTEGER: "integer",
	INTEGER_LIST: "integerlist",
	INTEGER_VECT: "integervector",
	INTEGER_SET: "integerset",
	INTEGER_MAP: "integermap",

	REF: "ref",
	REF_LIST: "reflist",
	REF_VECT: "refvector",
	REF_SET: "refset",
	REF_MAP: "refmap",

	STRING: "string",
	STRING_LIST: "stringlist",
	STRING_VECT: "stringvector",
	STRING_SET: "stringset",
	STRING_MAP: "stringmap",

	TIME: "time",
	TIME_LIST: "timelist",
	TIME_VECT: "timevector",
	TIME_SET: "timeset",
	TIME_MAP: "timemap",

	URL: "url",
	URL_LIST: "urllist",
	URL_VECT: "urlvector",
	URL_SET: "urlset",
	URL_MAP: "urlmap",

	// discontinued
	FLOAT: "float",
} as const;

/** reference to a node */
export type cnl = string;

/** reference to a node along a path */
export type cnlPath = [cnl, string];

/** primitive attribute value types */
export type C8PrimitiveValue = boolean | number | string | cnl;

/** attribute types */
export type C8AttributeType = typeof VALUE_TYPE[keyof typeof VALUE_TYPE];

/** attribute value types */
export type C8AttributeValue = C8PrimitiveValue | C8PrimitiveValue[] | Record<string, C8PrimitiveValue>;

/** form field key value pair, enforce id property to satisfy design system */
export type C8FieldSelectValue = string | string[];

/** form field value types */
export type C8FieldValue = C8PrimitiveValue | C8PrimitiveValue[];

/** unique identifier for attribute along a typed path of form <AttributeId>:(StartClass)Child:RelationId(EndClass)... */
export type C8AttributeKey = string;

/** date or time */
export type DateOrTime = Date | number;

/** date range array */
export type C8DateRangeArray = [number?, number?, DateRangeSelection?, number?];

/** form field attribute types */
export type C8FieldType =
	"boolean" |

	"integer" |

	"double" |

	"enum" |
	"enumlist" |
	"enumset" |

	"ref" |
	"reflist" |
	"refset" |
	"refvector" |

	"string" |
	"stringlist" |
	"stringset" |

	"time" |

	"url"
	;

/** convert given attribute value into array of primitive values */
export function attributeValueAsArray(value: C8AttributeValue): C8PrimitiveValue[] {
	if (isNullOrUndefined(value)) {
		return [];
	}
	if (Array.isArray(value)) {
		return value;
	}
	return typeof value === "object" ? Object.values(value) : [value];
}

/** return list value type (enumlist) for any given type */
export function getListValueType(type: C8AttributeType): C8AttributeType {
	return (getPrimitiveValueType(type) + "list") as C8AttributeType;
}

/** return primitive value type (enum) for given type (enum or enumlist) */
export function getPrimitiveValueType(type: C8AttributeType): C8AttributeType {
	return type.replace(RE_CONTAINER, "") as C8AttributeType;
}

/** return primitive value type (enum) for given container type (enumlist); return null if type is not a container type */
export function getPrimitiveValueTypeIfContainer(type: C8AttributeType): C8AttributeType | null {
	const primitiveType = getPrimitiveValueType(type);
	return (type === primitiveType) ? null : primitiveType;
}

/** return true if given value type is a container (list, set, map, vector) */
export function isContainerValueType(type: C8AttributeType): boolean {
	return RE_CONTAINER.test(type);
}

/** return true if primitive value type is enum */
export function isEnumValueType(type: C8AttributeType): boolean {
	return getPrimitiveValueType(type) === VALUE_TYPE.ENUM;
}

/** return true if given value type is a map */
export function isMapValueType(type: C8AttributeType): boolean {
	return RE_MAP.test(type);
}

/** return true if primitive value type is ref */
export function isRefValueType(type: C8AttributeType): boolean {
	return getPrimitiveValueType(type) === VALUE_TYPE.REF;
}
